import type { GraphKind } from '@/lib/apiTypes'
import {
  ALL_KINDS, ALL_RISKS, type FilterState, type RiskFilter, type OwnershipFilter, type Depth,
} from '@/features/graph/lib/graphView'
import { Tooltip } from '@/shared/components/Tooltip'
import { ChipIcon, DatabaseIcon, BoxIcon } from '@/shared/components/Icons'
import s from '@/features/graph/components/GraphFilterRail.module.css'

/**
 * The filter rail: a collapsible column on the LEFT of the canvas.
 *
 * Left, not right, because the detail panel already owns the right edge as a sibling flex
 * child — putting both there would have meant turning the panel into an overlay and having
 * them compete. On the left they coexist and the canvas keeps the middle.
 *
 * Every control is backed by a field the server actually sends. Three of the five chips on the
 * page this replaces could never match anything, because the fields they read were declared in
 * the client type but never populated by the endpoint.
 */

const KIND_GLYPH: Record<GraphKind, React.ReactNode> = {
  state: <DatabaseIcon size={12} />,
  module: <BoxIcon size={12} />,
  provider: <ChipIcon size={12} />,
}

const KIND_LABEL: Record<GraphKind, string> = {
  state: 'State files',
  module: 'Modules',
  provider: 'Providers',
}

const RISK_LABEL: Record<RiskFilter, string> = {
  blocked: 'Blocked',
  deprecated: 'Deprecated',
  high_risk: 'Critical / high',
  policy_failed: 'Policy failed',
  outdated: 'Outdated pin',
}

const RISK_TIP: Record<RiskFilter, string> = {
  blocked: 'Refused to consumers — by an admin kill switch, an enforcing policy, or a serve override',
  deprecated: 'Marked deprecated by its owner',
  high_risk: 'The latest published version carries critical or high severity scan findings',
  policy_failed: 'The latest published version fails at least one org policy',
  outdated: 'A workspace pins a version older than the newest one its constraint allows',
}

const RISK_COLOUR: Record<RiskFilter, string> = {
  blocked: 'var(--red)',
  deprecated: 'var(--amber)',
  high_risk: 'var(--red)',
  policy_failed: 'var(--accent)',
  outdated: 'var(--amber)',
}

export interface GraphFilterRailProps {
  filters: FilterState
  counts: {
    kinds: Record<GraphKind, number>
    risk: Record<RiskFilter, number>
    ownership: Record<OwnershipFilter, number>
    providers: Map<string, number>
    vendors: Map<string, number>
    namespacesByType: Map<string, Set<string>>
  }
  depth: Depth
  onDepth: (d: Depth) => void
  /** Depth only means something inside an isolated lineage. */
  depthEnabled: boolean
  hiddenCount: number
  onUnhideAll: () => void
  onToggleKind: (k: GraphKind) => void
  onToggleRisk: (r: RiskFilter) => void
  onToggleOwnership: (o: OwnershipFilter) => void
  onToggleProvider: (p: string) => void
  onToggleVendor: (v: string) => void
  onToggleOrphans: () => void
  collapsed: boolean
  onCollapse: (v: boolean) => void
}

function Check({
  label, suffix, tip, colour, count, active, disabled, onToggle, glyph,
}: {
  label: string
  /** Dimmed trailing detail (a disambiguating vendor). Truncates before the label does. */
  suffix?: string
  tip?: string
  colour?: string
  count: number
  active: boolean
  disabled: boolean
  onToggle: () => void
  glyph?: React.ReactNode
}) {
  const row = (
    <button
      type="button"
      className={`${s.row} ${active ? s.rowActive : ''}`}
      aria-pressed={active}
      disabled={disabled}
      onClick={onToggle}
    >
      {/* boxOn carries the default accent fill, so a control that passes no colour of its own
          still shows a visible tick. `colour` only overrides the shade. */}
      <span
        className={`${s.box} ${active ? s.boxOn : ''}`}
        style={active && colour ? { borderColor: colour, background: colour } : undefined}
      >
        {active && <span className={s.tick}>✓</span>}
      </span>
      {glyph && <span className={s.glyph}>{glyph}</span>}
      <span className={s.rowLabel} style={colour && !active ? { color: colour } : undefined}>{label}</span>
      {suffix && <span className={s.rowSuffix}>{suffix}</span>}
      <span className={s.count}>{count}</span>
    </button>
  )
  return tip ? <Tooltip text={tip} placement="right" maxWidth={260}>{row}</Tooltip> : row
}

export function GraphFilterRail({
  filters, counts, depth, onDepth, depthEnabled, hiddenCount, onUnhideAll,
  onToggleKind, onToggleRisk, onToggleOwnership, onToggleProvider, onToggleVendor,
  onToggleOrphans, collapsed, onCollapse,
}: GraphFilterRailProps) {
  if (collapsed) {
    return (
      <div className={s.railCollapsed}>
        <Tooltip text="Show filters" placement="right">
          <button className={s.expandBtn} aria-label="Show filters" onClick={() => onCollapse(false)}>
            ⫸
          </button>
        </Tooltip>
      </div>
    )
  }

  const providerTypes = [...counts.providers.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
  const vendors = [...counts.vendors.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
  const orphanDisabled = false

  return (
    <aside className={s.rail} aria-label="Graph filters">
      <div className={s.railHead}>
        <span className={s.railTitle}>Filters</span>
        <Tooltip text="Hide filters" placement="right">
          <button className={s.collapseBtn} aria-label="Hide filters" onClick={() => onCollapse(true)}>⫷</button>
        </Tooltip>
      </div>

      <div className={s.scroll}>
        <div className={s.group}>
          <div className={s.groupLabel}>Kind</div>
          {ALL_KINDS.map((k) => (
            <Check
              key={k}
              label={KIND_LABEL[k]}
              glyph={KIND_GLYPH[k]}
              count={counts.kinds[k]}
              active={filters.kinds.has(k)}
              disabled={counts.kinds[k] === 0 && !filters.kinds.has(k)}
              onToggle={() => onToggleKind(k)}
            />
          ))}
        </div>

        <div className={s.group}>
          <div className={s.groupLabel}>Risk</div>
          {ALL_RISKS.map((r) => (
            <Check
              key={r}
              label={RISK_LABEL[r]}
              tip={RISK_TIP[r]}
              colour={RISK_COLOUR[r]}
              count={counts.risk[r]}
              active={filters.risk.has(r)}
              disabled={counts.risk[r] === 0 && !filters.risk.has(r)}
              onToggle={() => onToggleRisk(r)}
            />
          ))}
        </div>

        <div className={s.group}>
          <div className={s.groupLabel}>Ownership</div>
          {(['internal', 'external'] as OwnershipFilter[]).map((o) => (
            <Check
              key={o}
              label={o === 'internal' ? 'This org' : 'External'}
              tip={o === 'internal'
                ? 'Artefacts this org owns and can govern'
                : 'Dependencies resolved outside this org — visible, but nothing here can change them'}
              count={counts.ownership[o]}
              active={filters.ownership.has(o)}
              disabled={counts.ownership[o] === 0 && !filters.ownership.has(o)}
              onToggle={() => onToggleOwnership(o)}
            />
          ))}
        </div>

        {providerTypes.length > 0 && (
          <div className={s.group}>
            <div className={s.groupLabel}>Provider</div>
            {providerTypes.map(([t, n]) => {
              const namespaces = [...(counts.namespacesByType.get(t) ?? [])].sort()
              // The type alone is the scannable token, so the vendor is appended ONLY where the
              // same type exists under more than one namespace. The full address is in the title
              // on every row regardless - that costs nothing and a tooltip cannot be scanned.
              const ambiguous = namespaces.length > 1
              return (
                <Check
                  key={t}
                  label={t}
                  suffix={ambiguous ? namespaces.join(', ') : undefined}
                  tip={namespaces.length > 0
                    ? `${namespaces.map((ns) => `${ns}/${t}`).join('  ·  ')}${ambiguous ? ' — this type exists under more than one vendor' : ''}`
                    : `Modules whose primary provider is ${t}`}
                  count={n}
                  active={filters.providers.has(t)}
                  disabled={n === 0 && !filters.providers.has(t)}
                  onToggle={() => onToggleProvider(t)}
                />
              )
            })}
          </div>
        )}

        {vendors.length > 0 && (
          <div className={s.group}>
            <div className={s.groupLabel}>Vendor</div>
            {vendors.map(([v, n]) => (
              <Check
                key={v}
                label={v}
                tip={`Everything from the ${v} namespace: its providers, and the modules whose primary provider it publishes. State files have no vendor, so they are unaffected.`}
                count={n}
                active={filters.vendors.has(v)}
                disabled={n === 0 && !filters.vendors.has(v)}
                onToggle={() => onToggleVendor(v)}
              />
            ))}
          </div>
        )}

        <div className={s.group}>
          <div className={s.groupLabel}>Readability</div>
          <Check
            label="Hide unconnected"
            tip="Most state workspaces have no lock file, so they have no provider edges at all. This collapses them out of the way — they stay in the count."
            count={0}
            active={filters.hideOrphans}
            disabled={orphanDisabled}
            onToggle={onToggleOrphans}
          />

          <div className={s.depthRow}>
            <span className={s.depthLabel}>Depth</span>
            <div className={s.seg} role="group" aria-label="Hops from the isolated node">
              {([1, 2, 'all'] as Depth[]).map((d) => (
                <button
                  key={String(d)}
                  type="button"
                  className={`${s.segBtn} ${depth === d ? s.segBtnOn : ''}`}
                  aria-pressed={depth === d}
                  disabled={!depthEnabled}
                  title={depthEnabled
                    ? `Show ${d === 'all' ? 'every hop' : `${d} hop${d === 1 ? '' : 's'}`} from the isolated node`
                    : 'Isolate a node first — depth counts hops from it'}
                  onClick={() => onDepth(d)}
                >
                  {d === 'all' ? 'all' : d}
                </button>
              ))}
            </div>
          </div>
        </div>

        {hiddenCount > 0 && (
          <div className={s.group}>
            <button className={s.unhideBtn} onClick={onUnhideAll}>
              Unhide {hiddenCount} node{hiddenCount === 1 ? '' : 's'}
            </button>
          </div>
        )}
      </div>
    </aside>
  )
}
